import { Link, useParams } from "react-router-dom";
import '../styles/NewsStyles/News.css';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppChat from "../components/WhatsAppChat";
import ArticleBody from "../components/NewsSection/ArticleBody";
import NewsCard from "../components/NewsSection/NewsCard";
import { formatPostDate, getPost, listPosts, unpublishedLabel } from "../news/posts";
import { usePageMeta } from "../hooks/usePageMeta";
import { webinarRepository } from "../webinars/repository";
import { useRepositoryData } from "../webinars/useRepositoryData";
import { isPastWebinar } from "../webinars/availability";
import { formatLongDate, formatTimeRange } from "../webinars/format";

const NewsArticle = () => {
    const { slug = "" } = useParams();
    const post = getPost(slug);
    usePageMeta(
        post ? post.seoTitle ?? `${post.title} | Kole Medical` : "Article not found | Kole Medical",
        post ? post.seoDescription ?? post.summary : undefined,
    );

    // Linked webinars that are public and haven't happened yet; a failed lookup just hides the list
    const related = post?.relatedWebinars ?? [];
    const { data: webinars = [] } = useRepositoryData(
        async () => {
            if (related.length === 0) return [];
            const all = await webinarRepository.listPublicWebinars();
            return all.filter(w => related.includes(w.slug) && w.status !== "cancelled" && !isPastWebinar(w));
        },
        `news-related|${related.join(",")}`,
    );

    if (!post) {
        return (
            <div>
                <Navbar />
                <div className="km-container nw-missing">
                    <h1 className="km-title">Article not found</h1>
                    <p className="km-lead">This article doesn't exist or is no longer published.</p>
                    <Link className="km-btn km-btn--primary" to="/news">See all news</Link>
                </div>
                <Footer />
                <WhatsAppChat />
            </div>
        );
    }

    const draft = unpublishedLabel(post);
    const more = listPosts().filter(p => p.slug !== post.slug).slice(0, 3);

    return (
        <div>
            <Navbar />
            <article className="nw-article">
                <header className="nw-article-head">
                    <div className="km-container nw-narrow km-enter">
                        <Link className="nw-back" to="/news">← All news</Link>
                        <p className="nw-meta">
                            {draft && <span className="nw-chip nw-chip--draft">{draft}</span>}
                            {post.category && <span className="nw-chip">{post.category}</span>}
                        </p>
                        <h1>{post.title}</h1>
                        <p className="nw-byline">
                            <time dateTime={post.publishedOn}>{formatPostDate(post.publishedOn)}</time>
                            <span aria-hidden="true"> · </span>
                            {post.author}
                        </p>
                    </div>
                </header>

                <div className="km-container nw-narrow">
                    {post.imageUrl && <img className="nw-article-image" src={post.imageUrl} alt={post.imageAlt ?? ""} />}
                    <p className="nw-standfirst">{post.summary}</p>
                    <ArticleBody blocks={post.body} />

                    {webinars.length > 0 && (
                        <aside className="nw-related" aria-labelledby="nw-related-title">
                            <h2 id="nw-related-title">Related webinars</h2>
                            <ul>
                                {webinars.map(w => (
                                    <li key={w.id}>
                                        <Link to={`/events/${w.slug}`}>{w.title}</Link>
                                        <small>{formatLongDate(w.startAt)}, {formatTimeRange(w.startAt, w.endAt)} SAST</small>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                    )}
                </div>
            </article>

            {more.length > 0 && (
                <section className="km-section km-section--alt" aria-labelledby="nw-more-title">
                    <div className="km-container">
                        <h2 className="km-title nw-more-title" id="nw-more-title">More news</h2>
                        <div className="km-cardRow nw-grid">
                            {more.map((p, index) => <NewsCard key={p.slug} post={p} revealOrder={index} />)}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
            <WhatsAppChat />
        </div>
    );
};

export default NewsArticle;
